import React, { useState, useEffect, memo } from 'react';
import { Menu, X, ChevronRight, Briefcase, Users, BookOpen, Heart, ArrowRight, Mail, Linkedin, Twitter, Zap, Globe, Rocket } from 'lucide-react';
import phoenixhrLogo from './assets/images/PHOENIXHR+.png';
import phoenixvertexLogo from './assets/images/PHOENIXVERTEX.png';

const NavigationMenu = memo(({ isOpen, setIsOpen, scrolled }) => (
  <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-gray-900/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center h-20">
        <div className="flex items-center space-x-3">
          <img
            src={phoenixvertexLogo}
            alt="Phoenix Vertex Logo"
            className="w-12 h-12 object-contain"
          />
          <div>
            <div className="text-2xl font-bold bg-gradient-to-r from-red-500 to-orange-400 bg-clip-text text-transparent">
              Phoenix Vertex
            </div>
            <div className="text-xs text-gray-400 -mt-1">Technologies</div>
          </div>
        </div>

        <div className="hidden md:flex items-center space-x-8">
          <a href="#home" className="text-gray-300 hover:text-red-500 transition-colors">Home</a>
          <a href="#products" className="text-gray-300 hover:text-red-500 transition-colors">Products</a>
          <a href="#about" className="text-gray-300 hover:text-red-500 transition-colors">About</a>
          <a href="#contact" className="text-gray-300 hover:text-red-500 transition-colors">Contact</a>
        </div>

        <button
          className="md:hidden text-white"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle mobile menu"
        >
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>
    </div>

    {isOpen && (
      <div className="md:hidden bg-gray-900/98 backdrop-blur-sm border-t border-red-900/50">
        <div className="px-4 py-6 space-y-4">
          <a href="#home" className="block text-gray-300 hover:text-red-500 transition-colors py-2">Home</a>
          <a href="#products" className="block text-gray-300 hover:text-red-500 transition-colors py-2">Products</a>
          <a href="#about" className="block text-gray-300 hover:text-red-500 transition-colors py-2">About</a>
          <a href="#contact" className="block text-gray-300 hover:text-red-500 transition-colors py-2">Contact</a>
        </div>
      </div>
    )}
  </nav>
));

NavigationMenu.displayName = 'NavigationMenu';

const ProductCard = memo(({ icon: Icon, title, description, status, link, image }) => (
  <div className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-8 border border-red-900/30 hover:border-red-500/50 transition-all duration-300 shadow-lg hover:shadow-xl">
    <div className="flex items-start justify-between mb-4">
      {image ? (
        <img src={image} alt={title} className="w-14 h-14 object-contain" />
      ) : (
        <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-orange-500 rounded-lg flex items-center justify-center shadow-lg shadow-red-500/50 group-hover:scale-110 transition-transform">
          <Icon size={28} className="text-white" />
        </div>
      )}
      <span className={`text-xs font-semibold px-3 py-1 rounded-full ${status === 'Live' ? 'bg-green-500/20 text-green-400 border border-green-500/50' : 'bg-blue-500/20 text-blue-400 border border-blue-500/50'}`}>
        {status}
      </span>
    </div>
    <h3 className="text-2xl font-bold text-white mb-2">{title}</h3>
    <p className="text-gray-400 text-sm leading-relaxed mb-6">{description}</p>
    {link && (
      <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors font-semibold text-sm">
        Learn More
        <ArrowRight size={16} />
      </a>
    )}
  </div>
));

ProductCard.displayName = 'ProductCard';

export default function PhoenixVertexHomepage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-red-950 to-gray-900 text-white">
      <NavigationMenu isOpen={isMenuOpen} setIsOpen={setIsMenuOpen} scrolled={scrolled} />

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <div className="inline-block mb-6">
              <div className="px-4 py-2 bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-full text-red-400 text-sm font-medium">
                Building SaaS for Africa
              </div>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-red-500 to-orange-400 bg-clip-text text-transparent">
                Enterprise Software
              </span>
              <br />
              <span className="text-white">Made Simple for Startups</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-400 mb-10 max-w-3xl mx-auto">
              Phoenix Vertex creates beautifully simple SaaS products that help African startups and growing companies scale faster. No complexity. No enterprise bloat.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#products"
                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 rounded-lg font-semibold text-lg transition-all shadow-lg shadow-red-500/30"
              >
                Explore Products
                <ChevronRight className="ml-2" size={20} />
              </a>
              <a
                href="#contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-red-500 hover:bg-red-500/10 rounded-lg font-semibold text-lg transition-all"
              >
                Get in Touch
              </a>
            </div>
          </div>

          {/* Floating Elements */}
          <div className="mt-20 relative">
            <div className="absolute top-0 left-1/4 w-64 h-64 bg-red-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
            <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-orange-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                About <span className="bg-gradient-to-r from-red-500 to-orange-400 bg-clip-text text-transparent">Phoenix Vertex</span>
              </h2>
              <p className="text-xl text-gray-400 mb-6 leading-relaxed">
                We're an African software company building a suite of SaaS products designed specifically for startups and growing businesses across the continent.
              </p>
              <p className="text-lg text-gray-400 mb-6 leading-relaxed">
                Most business software is bloated, expensive, and built for enterprises. We're changing that. Our mission is to create category-defining products that are intuitive, affordable, and powerful enough to grow with your business.
              </p>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                We start with deep customer empathy. We listen to founders and operators, understand their real challenges, and build products that solve those problems elegantly.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-red-900/30">
                <Rocket className="w-8 h-8 text-red-500 mb-3" />
                <h3 className="text-lg font-bold text-white mb-2">Our Vision</h3>
                <p className="text-gray-400 text-sm">To be the go-to SaaS partner for African startups and growing companies looking to scale without enterprise complexity.</p>
              </div>
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-red-900/30">
                <Zap className="w-8 h-8 text-orange-500 mb-3" />
                <h3 className="text-lg font-bold text-white mb-2">Our Approach</h3>
                <p className="text-gray-400 text-sm">Simple. Powerful. Fast. We build products you can learn in minutes, not months. No unnecessary features. Just what you actually need.</p>
              </div>
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-red-900/30">
                <Globe className="w-8 h-8 text-red-500 mb-3" />
                <h3 className="text-lg font-bold text-white mb-2">Africa First</h3>
                <p className="text-gray-400 text-sm">Built for African businesses. We understand local regulations, compliance requirements, and the unique challenges of operating in our markets.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Our <span className="bg-gradient-to-r from-red-500 to-orange-400 bg-clip-text text-transparent">Products</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              A suite of simple, powerful SaaS tools built for startups and growing businesses
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <ProductCard
              icon={Users}
              title="PhoenixHR Plus"
              description="Manage recruitment, payroll, and your team without any HR background. Get set up in minutes and focus on growing your business."
              status="Live"
              link="https://hr.phoenixvertex.com"
              image={phoenixhrLogo}
            />
            <ProductCard
              icon={BookOpen}
              title="Phoenix School"
              description="Modern school management system that simplifies administration, student records, academic tracking, and parent communication."
              status="Coming Soon"
              link={null}
            />
            <ProductCard
              icon={Heart}
              title="Phoenix Health"
              description="Healthcare management system for clinics and facilities. Patient records, appointments, and operational efficiency in one place."
              status="Coming Soon"
              link={null}
            />
          </div>
        </div>
      </section>

      {/* Why Startups Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why Startups <span className="bg-gradient-to-r from-red-500 to-orange-400 bg-clip-text text-transparent">Choose Us</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-orange-500 rounded-lg flex items-center justify-center shadow-lg shadow-red-500/50">
                  <Zap size={24} className="text-white" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Quick to Deploy</h3>
                <p className="text-gray-400">No implementation consultants. No 6-month onboarding. You're up and running in days, not months.</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-orange-500 rounded-lg flex items-center justify-center shadow-lg shadow-red-500/50">
                  <Briefcase size={24} className="text-white" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Enterprise Grade, Startup Friendly</h3>
                <p className="text-gray-400">Professional-grade software without the professional-grade price tag. Free plan for teams of 1-5, plus 3 affordable tiers as you scale. No long-term contracts.</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-orange-500 rounded-lg flex items-center justify-center shadow-lg shadow-red-500/50">
                  <Users size={24} className="text-white" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Built for Non-Experts</h3>
                <p className="text-gray-400">You don't need specialized training or dedicated staff. Anyone on your team can learn and use our products intuitively.</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-orange-500 rounded-lg flex items-center justify-center shadow-lg shadow-red-500/50">
                  <Globe size={24} className="text-white" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Africa-Compliant</h3>
                <p className="text-gray-400">Built-in compliance with local tax laws, labor regulations, and data protection requirements. No surprises.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Let's <span className="bg-gradient-to-r from-red-500 to-orange-400 bg-clip-text text-transparent">Build Together</span>
          </h2>
          <p className="text-xl text-gray-400 mb-4">
            Whether you're interested in our products, want to partner with us, or have feedback—we'd love to hear from you.
          </p>
          <p className="text-sm text-gray-400 mb-8">
            Phone: <span className="font-semibold text-gray-200">+2348124951927</span> • Company Registration (CAC): <span className="font-semibold text-gray-200">RC - 9071301</span>
          </p>

          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-red-900/30 shadow-xl">
            <div className="grid md:grid-cols-3 gap-8">
              <a href="mailto:contact@phoenixvertex.com" className="flex flex-col items-center p-6 hover:bg-red-500/10 rounded-xl transition-all group">
                <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-orange-600 rounded-full flex items-center justify-center mb-4 shadow-lg shadow-red-500/50 group-hover:shadow-red-500/70 transition-all">
                  <Mail size={28} className="text-white" />
                </div>
                <h3 className="font-semibold mb-2 text-white group-hover:text-red-400 transition-colors">Email</h3>
                <p className="text-gray-400 text-sm">contact@phoenixvertex.com</p>
              </a>

              <a href="https://www.linkedin.com/company/phoenix-vertex-technologies" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center p-6 hover:bg-red-500/10 rounded-xl transition-all group">
                <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-orange-600 rounded-full flex items-center justify-center mb-4 shadow-lg shadow-red-500/50 group-hover:shadow-red-500/70 transition-all">
                  <Linkedin size={28} className="text-white" />
                </div>
                <h3 className="font-semibold mb-2 text-white group-hover:text-red-400 transition-colors">LinkedIn</h3>
                <p className="text-gray-400 text-sm">Follow our journey</p>
              </a>

              <a href="https://twitter.com/phoenixvertex" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center p-6 hover:bg-red-500/10 rounded-xl transition-all group">
                <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-orange-600 rounded-full flex items-center justify-center mb-4 shadow-lg shadow-red-500/50 group-hover:shadow-red-500/70 transition-all">
                  <Twitter size={28} className="text-white" />
                </div>
                <h3 className="font-semibold mb-2 text-white group-hover:text-red-400 transition-colors">Twitter</h3>
                <p className="text-gray-400 text-sm">Latest updates</p>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Legal & Compliance Section */}
      <section id="legal" className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-900/50">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
          <div>
            <h3 className="text-2xl font-bold mb-4">Privacy & Terms</h3>
            <div id="privacy-policy" className="mb-6">
              <h4 className="text-lg font-semibold text-white mb-2">Privacy Policy</h4>
              <p className="text-sm text-gray-400 leading-relaxed">
                We only collect the information needed to operate our products and services. We do not sell or rent your data. Access to customer data is strictly limited to authorized personnel, and we apply industry-standard security controls to protect it.
              </p>
            </div>
            <div id="terms-of-service" className="mb-6">
              <h4 className="text-lg font-semibold text-white mb-2">Terms of Service</h4>
              <p className="text-sm text-gray-400 leading-relaxed">
                Our products and services are provided to businesses and organizations. Customers are responsible for ensuring their use complies with applicable laws. Abuse, including unauthorized access or misuse, is prohibited and may result in account termination.
              </p>
            </div>
          </div>
          <div>
            <h3 className="text-2xl font-bold mb-4">Data Protection</h3>
            <div id="data-protection" className="mb-6">
              <h4 className="text-lg font-semibold text-white mb-2">Data Protection (GDPR / NDPR)</h4>
              <p className="text-sm text-gray-400 leading-relaxed">
                We design our products with data protection principles aligned to international frameworks such as GDPR and Nigeria's NDPR, including data minimization, access control, and auditability. Customers may request access, correction, or deletion of personal data, subject to applicable legal and regulatory requirements.
              </p>
            </div>
            <p className="text-xs text-gray-500">
              For privacy or data protection questions, contact us at{' '}
              <a href="mailto:contact@phoenixvertex.com" className="text-red-400 underline">contact@phoenixvertex.com</a>.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <img
                src={phoenixvertexLogo}
                alt="Phoenix Vertex Logo"
                className="w-10 h-10 object-contain"
              />
              <div>
                <div className="text-xl font-bold text-white">Phoenix Vertex</div>
                <div className="text-xs text-gray-400">Technologies</div>
              </div>
            </div>

            <div className="text-gray-400 text-sm text-center md:text-right">
              <p className="mb-2">© 2024 Phoenix Vertex Technologies. All rights reserved.</p>
              <p>Building simple, powerful SaaS for African startups.</p>
              <div className="flex flex-wrap gap-3 justify-center md:justify-end text-xs mt-2">
                <a href="#privacy-policy" className="hover:text-red-400 transition-colors">Privacy Policy</a>
                <span className="text-gray-600">•</span>
                <a href="#terms-of-service" className="hover:text-red-400 transition-colors">Terms of Service</a>
                <span className="text-gray-600">•</span>
                <a href="#data-protection" className="hover:text-red-400 transition-colors">Data Protection</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
